import React, { useEffect, useRef } from 'react';
import { Smartphone, Shield, Activity, Clock } from 'lucide-react';
import './MobileAppSection.css';
import DriviLogo from '../assets/logo.svg';

const MobileAppSection = ({ language = 'en', t }) => {
  const mobileAppRef = useRef(null);
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (mobileAppRef.current) {
        const rect = mobileAppRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        setMousePosition({ x: x - 0.5, y: y - 0.5 });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="py-24 bg-gradient-to-br from-[#3498DB]/5 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-[#3498DB]/10 rounded-full text-[#3498DB] font-medium mb-6">
              {t.mobileApp.badge}
            </div>
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-6">
              {t.mobileApp.title}
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              {t.mobileApp.subtitle}
            </p>

            <div className="grid grid-cols-2 gap-6 mb-12">
              <div className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-[#3498DB]/10 rounded-lg flex items-center justify-center mb-4">
                  <Smartphone className="w-6 h-6 text-[#3498DB]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">
                  {t.mobileApp.features.crossPlatform.title}
                </h3>
                <p className="text-gray-600">
                  {t.mobileApp.features.crossPlatform.description}
                </p>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-[#3498DB]/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#3498DB]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">
                  {t.mobileApp.features.secureAccess.title}
                </h3>
                <p className="text-gray-600">
                  {t.mobileApp.features.secureAccess.description}
                </p>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-[#3498DB]/10 rounded-lg flex items-center justify-center mb-4">
                  <Activity className="w-6 h-6 text-[#3498DB]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">
                  {t.mobileApp.features.realTime.title}
                </h3>
                <p className="text-gray-600">
                  {t.mobileApp.features.realTime.description}
                </p>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-[#3498DB]/10 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-[#3498DB]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">
                  {t.mobileApp.features.fullControl.title}
                </h3>
                <p className="text-gray-600">
                  {t.mobileApp.features.fullControl.description}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-[#3498DB] text-white px-6 py-3 rounded-lg hover:bg-[#2980B9] transition-colors flex items-center justify-center">
                {t.mobileApp.cta.ios}
              </button>
              <button className="bg-[#2c3e50] text-white px-6 py-3 rounded-lg hover:bg-[#2c3e50]/90 transition-colors flex items-center justify-center">
                {t.mobileApp.cta.android}
              </button>
            </div>
          </div>

          {/* Right side - Mobile Device with Google Maps Style Interface */}
          <div ref={mobileAppRef} className="relative">
            <div 
              className="mobile-device-animation"
              style={{
                transform: `perspective(1000px) rotateY(${mousePosition.x * 20}deg) rotateX(${mousePosition.y * -20}deg)`
              }}
            >
              <svg
                viewBox="0 0 320 640"
                className="w-full max-w-sm mx-auto drop-shadow-2xl"
              >
                {/* Phone frame */}
                <rect x="0" y="0" width="320" height="640" rx="40" fill="#2c3e50" />
                <rect x="12" y="12" width="296" height="616" rx="32" fill="#fff" />
                
                {/* App header */}
                <g className="app-header">
                  <rect x="24" y="24" width="272" height="60" rx="12" fill="#ffffff" />
                  <image href={DriviLogo} x="36" y="36" width="36" height="36" className="app-logo" />
     
                </g>

                {/* Map container */}
                <g>
                  <rect className="map-container" x="24" y="96" width="272" height="420" rx="12" />
                  
                  {/* Map areas */}
                  <rect className="map-park" x="40" y="180" width="80" height="60" rx="4" />
                  <rect className="map-water" x="180" y="280" width="100" height="40" rx="4" />
                  
                  {/* Roads */}
                  <path 
                    d="M24 200 L296 200" 
                    className="map-road"
                    strokeLinecap="round"
                  />
                  <path 
                    d="M24 300 L296 300" 
                    className="map-road"
                    strokeLinecap="round"
                  />
                  <path 
                    d="M160 96 L160 516" 
                    className="map-road"
                    strokeLinecap="round"
                  />
                  
                  {/* Secondary roads */}
                  <path 
                    d="M80 96 L80 516" 
                    className="map-road-secondary"
                    strokeLinecap="round"
                  />
                  <path 
                    d="M240 96 L240 516" 
                    className="map-road-secondary"
                    strokeLinecap="round"
                  />

                  {/* Vehicles */}
                  <g>
                    {/* Delivery vehicle */}
                    <circle 
                      r="6" 
                      className="vehicle-marker vehicle-delivery vehicle-marker-pulse"
                      style={{"--duration": "15s"}}
                    >
                      <animateMotion
                        path="M40 200 L280 200"
                        dur="15s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    {/* Service vehicle */}
                    <circle 
                      r="6" 
                      className="vehicle-marker vehicle-service vehicle-marker-pulse"
                      style={{"--duration": "20s"}}
                    >
                      <animateMotion
                        path="M160 120 L160 480"
                        dur="20s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    {/* Emergency vehicle */}
                    <circle 
                      r="6" 
                      className="vehicle-marker vehicle-emergency vehicle-marker-pulse"
                      style={{"--duration": "12s"}}
                    >
                      <animateMotion
                        path="M240 480 L240 120"
                        dur="12s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </g>
                </g>

                {/* Bottom sheet with vehicle info */}
                <g className="bottom-sheet">
                  <rect x="24" y="528" width="272" height="88" rx="12" fill="#ffffff" />
                  <text x="40" y="556" fill="#2c3e50" fontSize="14" fontWeight="600">Active Vehicles</text>
                  
                  {/* Vehicle status indicators */}
                  <g transform="translate(40, 576)">
                    <circle cx="0" cy="0" r="4" className="vehicle-delivery" />
                    <text x="12" y="4" fill="#64748b" fontSize="12">Delivery (3)</text>
                    
                    <circle cx="80" cy="0" r="4" className="vehicle-service" />
                    <text x="92" y="4" fill="#64748b" fontSize="12">Service (2)</text>
                    
                    <circle cx="160" cy="0" r="4" className="vehicle-emergency" />
                    <text x="172" y="4" fill="#64748b" fontSize="12">Emergency (1)</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileAppSection; 